import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import {
  SaveOutlined,
  CloseOutlined,
  PlusCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Table,
  Spin,
  Form,
  Input,
  message,
  Col,
  Row,
  InputNumber,
  Card,
} from "antd";
import {
  deleteMapServiceBranch,
  getListMapServiceBranch,
  updateMapServiceBranch,
} from "../../../api/mapServiceBranch/mapServiceBranch";
import {
  setCurrentPage,
  setPageSize,
} from "../../../redux/mapServiceBranch/mapServiceBranch";
import FormAddMapServiceBranch from "./FormAddMapServiceBranch";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import { neutral } from "../../../theme/tokens";

export default function MapServiceBranchTable() {
  const {
    listMapServiceBranchs,
    isLoadMapServiceBranch,
    currentPage,
    pageSize,
    error,
    totalRecords,
  } = useSelector((state) => state.persistedReducer.mapServiceBranchSlice);

  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [editedRecord, setEditedRecord] = useState({});

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  useEffect(() => {
    dispatch(getListMapServiceBranch({ currentPage, pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleSizeChange = useCallback(
    (current, size) => {
      setPageSize(size);
    },
    [setPageSize]
  );

  // Kiểm tra xem dòng có đang được chỉnh sửa không
  const isEditing = (record) => record.id === editingKey;

  // Bắt đầu chỉnh sửa
  const edit = (record) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id);
    setEditedRecord(record);
  };

  // Hủy chỉnh sửa
  const cancel = () => {
    setEditingKey("");
  };

  // Lưu dữ liệu đã chỉnh sửa
  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const updatedData = { ...row };
      const response = await dispatch(
        updateMapServiceBranch({ id, data: updatedData })
      );
      if (updateMapServiceBranch.fulfilled.match(response)) {
        message.success("Cập nhât dữ liệu thành công!");
        await dispatch(getListMapServiceBranch({ currentPage, pageSize }));
      } else if (updateMapServiceBranch.rejected.match(response)) {
        if (response.payload.message) {
          message.error(response.payload.message);
        }
        if (response.payload.errors) {
          Object.keys(response.payload.errors).forEach((field) => {
            const errorMessages = response.payload.errors[field];
            errorMessages.forEach((errorMessage) => {
              message.error(`${errorMessage}`);
            });
          });
        }

        if (response.payload.error) {
          message.error(response.payload.error);
        }
      }
      setEditingKey("");
    } catch (err) {
      console.error("Có lỗi xảy ra:", err);
    }
  };

  const deleteEventRecord = async (id) => {
    try {
      await dispatch(deleteMapServiceBranch(id));
      dispatch(getListMapServiceBranch({ currentPage, pageSize }));
    } catch (err) {
      console.error("Error deleting service:", err);
    }
  };

  // Định nghĩa các cột cho bảng
  const columns = [
    {
      title: "Dịch vụ",
      dataIndex: "service_id",
      key: "service_id",
      fixed: 'left',
      width: 200,
      ellipsis: true,
      render: (_, record) => record.service?.name || "Không có dữ liệu",
    },
    {
      title: "Chi nhánh",
      dataIndex: "mcoupon_branch_id",
      key: "mcoupon_branch_id",
      width: 200,
      ellipsis: true,
      render: (_, record) => record.branch?.name || "Không có dữ liệu",
    },
    {
      title: "Mã doanh nghiệp (ID)",
      dataIndex: "business_id",
      key: "business_id",
      width: 200,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="business_id"
            rules={[
              {
                required: false,
                message: "Mã doanh nghiệp",
              },
            ]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.business_id
        );
      },
    },
    {
      title: "Verify Token webhook",
      dataIndex: "verify_token",
      key: "verify_token",
      align: "center",
      width: 150,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="verify_token"
            rules={[
              {
                required: false,
                message: "Mã doanh nghiệp",
              },
            ]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.verify_token
        );
      },
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      align: "center",
      width: 100,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="version"
            rules={[
              {
                required: false,
                message: "Version",
              },
            ]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.version
        );
      },
    },
    {
      title: "APPID",
      dataIndex: "appId",
      key: "appId",
      align: "center",
      width: 100,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="appId"
            rules={[
              {
                required: false,
                message: "appId",
              },
            ]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.appId
        );
      },
    },
    {
      title: "AccessToken",
      dataIndex: "accessToken",
      key: "accessToken",
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        return isEditing(record) ? (
          <Form.Item
            name="accessToken"
            rules={[{ required: false, message: "accessToken" }]}
            style={{ margin: 0 }}
          >
            <Input />
          </Form.Item>
        ) : (
          record.accessToken ? (
            <>
              <span>{record.accessToken.slice(0, 10)}...</span>
              <Button 
                type="link" 
                icon={<CopyOutlined />}
                onClick={() => navigator.clipboard.writeText(record.accessToken)}
                style={{ marginLeft: 8 }}
              >
                Sao chép
              </Button>
            </>
          ) : (
            <span></span> // Hiển thị nếu không có accessToken
          )
        );
      },
    },
    
    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "center",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        const editable = isEditing(record); // Kiểm tra nếu dòng đang chỉnh sửa
        return editable ? (
          <>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => save(record.id)} // Lưu dữ liệu
              style={{ marginRight: 8 }}
            >
              Lưu
            </Button>
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={cancel} // Hủy chỉnh sửa
              danger
              style={{ color: neutral[500] }}
            >
              Hủy
            </Button>
          </>
        ) : (
          <RowActions
            disabled={editingKey !== ""}
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                onClick: () => edit(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa thiết lập",
                danger: true,
                divider: true,
                onClick: () =>
                  confirmDelete(`thiết lập #${record.id}`, () =>
                    deleteEventRecord(record.id)
                  ),
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <div>
      <div className="bg-gray-50 min-h-screen p-6">
        <Card className="mb-6 shadow-sm">
          {/* <h2>Thiết lập liên kết dịch vụ</h2> */}
          <Breadcrumb separator=">">
            <Breadcrumb.Item>Trang chủ</Breadcrumb.Item>
            <Breadcrumb.Item>Cài đặt</Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to="/map-service-branchs">
                Thiết lập liên kết dịch vụ
              </NavLink>
            </Breadcrumb.Item>
          </Breadcrumb>

          <p style={{ fontSize: "12px", color: "#555" }}>
            Trang <strong>"Thiết lập liên kết dịch vụ"</strong> cho phép quản
            trị viên cấu hình và quản lý các liên kết giữa hệ thống tích điểm và
            các hệ thống bên ngoài (như các đơn vị đối tác). Khi hệ thống tích
            điểm nhận được dữ liệu từ các hệ thống khác, các mã đơn vị hoặc
            token cần được xác thực để đảm bảo dữ liệu hợp lệ và thuộc về cửa
            hàng cụ thể trong hệ thống.
          </p>
          <Divider />
          <Row gutter={[16, 16]}>
            <Col xl lg md sm>
              <Button
                type="primary"
                onClick={showModal}
                icon={<PlusCircleOutlined />}
              >
                Thêm mới
              </Button>
              <FormAddMapServiceBranch
                isModalOpen={isModalOpen}
                currentPage={currentPage}
                pageSize={pageSize}
                handleOk={handleOk}
                handleCancel={handleCancel}
              />
            </Col>
          </Row>
          <Divider />

          <Form form={form} component={false}>
            <Table
              columns={columns}
              loading={
                listMapServiceBranchs?.length === 0
                  ? isLoadMapServiceBranch
                  : false
              }
              dataSource={isLoadMapServiceBranch ? [] : listMapServiceBranchs} // Dữ liệu hiển thị trong bảng
              rowKey="id"
              pagination={{
                showTotal: (totalRecords, range) =>
                  `Hiển thị từ ${range[0]} -> ${range[1]} trong tổng ${totalRecords} giá trị`,
                showQuickJumper: true,
                showSizeChanger: true,
                onShowSizeChange: handleSizeChange,
                total: totalRecords,
                current: currentPage,
                onChange: handlePageChange,
                pageSizeOptions: [10, 20, 30],
              }}
              locale={{
                emptyText: <Empty description="Không có thông tin!" />,
              }}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </Form>
        </Card>
      </div>
    </div>
  );
}
